import type { ExploreResult } from "@/types";

const LEAD_ENVELOPE_RE = /<<lead:(\{.*?\})>>/g;
const OFFER_ENVELOPE_RE = /<<offer:(\{.*?\})>>/g;

export interface ParsedLead {
  url?: string;
  company: string;
  dealName?: string;
  title?: string;
  location?: string;
  estimatedValue?: string;
  compensation?: string;
  signal?: string;
  source?: string;
  why?: string;
  postedHint?: string;
  verification?: string;
}

function normalizeLead(raw: ParsedLead): ParsedLead | null {
  const company = raw.company?.trim();
  const dealName = raw.dealName?.trim() || raw.title?.trim();
  if (!company || !dealName) return null;
  return { ...raw, company, dealName };
}

function parseEnvelopes(text: string, re: RegExp): ParsedLead[] {
  const leads: ParsedLead[] = [];
  const pattern = new RegExp(re.source, "g");
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(text)) !== null) {
    try {
      const parsed = normalizeLead(JSON.parse(match[1]) as ParsedLead);
      if (parsed) leads.push(parsed);
    } catch {
      // skip malformed envelope
    }
  }
  return leads;
}

/** Extract lead envelopes from streamed AI text (supports legacy offer envelopes). */
export function parseLeadEnvelopes(text: string): ParsedLead[] {
  const fromLead = parseEnvelopes(text, LEAD_ENVELOPE_RE);
  if (fromLead.length > 0) return fromLead;
  return parseEnvelopes(text, OFFER_ENVELOPE_RE).map((o) => ({
    ...o,
    dealName: o.dealName ?? o.title,
    signal: o.why,
  }));
}

/** Strip lead/offer envelope lines from reasoning text shown to the user. */
export function stripLeadEnvelopes(text: string): string {
  return text
    .replace(/<<lead:\{.*?\}>>/g, "")
    .replace(/<<offer:\{.*?\}>>/g, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

export function leadToExploreResult(lead: ParsedLead, index: number): ExploreResult {
  const dealName = lead.dealName ?? lead.title ?? lead.company;
  const url = lead.url ?? `https://${lead.company.toLowerCase().replace(/\s+/g, "")}.example`;
  return {
    portal: lead.source ?? "ai-prospect",
    url,
    title: dealName,
    company: lead.company,
    location: lead.location,
    compensation: lead.estimatedValue ?? lead.compensation,
    postedAt: lead.postedHint,
    why: lead.why ?? lead.signal,
    source: lead.source ?? "ai-prospect",
    verification: lead.verification,
    id: `${url}-${index}`,
  };
}
